/**
 * What somebody is called, asked for at the one moment they are choosing it.
 *
 * **The field says "username" and the column is `user.name`.** That is the
 * first thing to know before reading the rest of this file. Better Auth owns
 * the `user` table and `name` is its column — renaming it breaks sign-in
 * rather than producing a type error, which `apps/api/src/db/schema/auth.ts`
 * says at length — so this is a rule about what we *ask for* and what we
 * *accept*, not a second place a name is kept.
 *
 * **It is deliberately not unique**, and that is a decision rather than an
 * omission. A username can only be unique if every account has one, and an
 * account made through Discord or Google arrives with whatever name the
 * provider hands over: we never ask, because asking would put a form in the
 * middle of the one route whose whole appeal is that there is no form. A
 * uniqueness rule half the accounts are exempt from is not a rule, it is a
 * race between the two halves — the first Discord account called `victor`
 * would take a name somebody had been promised was theirs.
 *
 * `creator_profile.handle` is the namespace that *is* unique, is checked
 * against `GET /v1/availability/creator-handle`, and is what
 * `goodbuild.cc/@victor` resolves. See `apps/api/src/lib/handle.ts`, which is
 * narrower than this on purpose: a handle is an address a stranger types, so
 * it is lowercase ASCII and a reserved list guards it against phishing. A
 * username is a word in a header.
 *
 * So what this buys is shape — something a person picked, that reads as a name
 * and cannot be a sentence, a URL or an empty string. `display-name.ts` is the
 * layer under it, deciding what any name is allowed to *be* once it is in the
 * system, including the ones we never asked for; this is the narrower question
 * asked of somebody who is typing one.
 */

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 24;

/**
 * Letters, numbers, underscore and hyphen — starting and ending on a letter or
 * a number.
 *
 * **Unicode rather than ASCII**, which is the one place this is wider than
 * `HANDLE_PATTERN` rather than narrower. A handle is an address and has to
 * survive being read down a phone; a username is what somebody is called, and
 * a store that sells games in eleven countries has no business telling a
 * player their own name is not spellable. `\p{M}` is in the interior set for
 * the same reason and it is not decoration: Devanagari, Thai and Arabic write
 * ordinary letters as a base plus a combining mark, and a class of `\p{L}`
 * alone refuses words those scripts spell every day.
 *
 * What is excluded, and why each:
 *
 * — **Spaces.** This is the difference the sign-up form is asking about. A
 *   name with a space in it is the thing we stopped asking for.
 * — **Emoji and symbols.** Unpronounceable, unsearchable, and a header has
 *   room for a word.
 * — **A leading or trailing `_` or `-`.** Both are legible in the middle and
 *   both read as damage at either end, which is where a truncated paste and a
 *   half-typed name leave them.
 *
 * The `u` flag makes `{1,22}` count code points rather than UTF-16 units, so
 * an astral character costs one against the length here exactly as it does in
 * `usernameProblem` below.
 */
export const USERNAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N}\p{M}_-]{1,22}[\p{L}\p{N}\p{M}]$/u;

/**
 * Names the platform keeps, because in somebody else's hands they are a claim
 * rather than a name.
 *
 * Shorter than `RESERVED_HANDLES` and for a narrower reason. That list guards
 * an *address* — `goodbuild.cc/@support` in a stranger's hands is a phishing
 * page — so it holds every word somebody might reach a page by. Nothing
 * resolves a username, so the only harm left is somebody being taken for us,
 * and this is that list: our name, and the words a player would believe if
 * they appeared beside a message.
 *
 * Compared lowercased, so `Support`, `SUPPORT` and `support` are one entry.
 * Not folded any further than that: refusing `5upport` means guessing at what
 * a reader would misread, and a list that guesses gets longer every week and
 * still refuses somebody's actual name.
 */
export const RESERVED_USERNAMES: ReadonlySet<string> = new Set([
  'admin',
  'administrator',
  'goodbuild',
  'mod',
  'moderator',
  'official',
  'root',
  'security',
  'staff',
  'support',
  'system',
  'team',
]);

/**
 * Why a username is unacceptable, or null when it is fine.
 *
 * One sentence, addressed to the person typing, in the voice the rest of the
 * sign-up is written in: short words, and the way out named rather than the
 * rule quoted. Read by the browser as somebody types and by nothing on the
 * server — see the note at the top of this file for why the server's job here
 * is bounds rather than shape.
 *
 * Length is counted in code points, matching the pattern's own counting. A
 * name written in an astral script is not three times too long.
 */
export function usernameProblem(username: string): string | null {
  const points = Array.from(username);

  if (points.length < USERNAME_MIN) return `A username needs ${USERNAME_MIN} characters or more.`;
  if (points.length > USERNAME_MAX) return `That is too long. ${USERNAME_MAX} characters at most.`;

  if (!USERNAME_PATTERN.test(username)) {
    return 'Letters and numbers, with _ or - in the middle. No spaces.';
  }

  if (RESERVED_USERNAMES.has(username.toLowerCase())) return 'That one is ours. Pick another.';

  return null;
}
